import React, { Component } from "react";
import {
  Table,
  Row,
  Col,
  Button,
  Spinner,
  Modal,
  Form,
  FormControl,
} from "react-bootstrap";
import CartContext from "../StoreManager/CartContext";
import { fetchCrmList, saveCrmItem, deleteCrmItem } from "./CrmApi";

const emptyTag = {
  tagId: 0,
  storeId: "",
  name: "",
  description: "",
};

export default class CrmTags extends Component {
  static contextType = CartContext;

  constructor(props) {
    super(props);
    this.state = {
      tags: [],
      filteredTags: [],
      loading: true,
      search: "",
      error: null,
      showModal: false,
      isEdit: false,
      currentTag: { ...emptyTag },
    };
  }

  componentDidMount() {
    this.loadTags();
  }

  loadTags = async () => {
    const storeId = this.context.storeId;
    if (!storeId) {
      this.setState({ loading: false, error: "StoreId is required." });
      return;
    }

    this.setState({ loading: true, error: null });
    try {
      const tags = await fetchCrmList("Tags", "GetTags", storeId);
      const normalized = Array.isArray(tags) ? tags : [];
      this.setState({
        tags: normalized,
        filteredTags: normalized,
        loading: false,
      });
    } catch (error) {
      this.setState({
        loading: false,
        error: error.message || "Unable to load tags.",
      });
    }
  };

  onSearchChange = (event) => {
    const search = event.target.value || "";
    const normalizedSearch = search.trim().toLowerCase();
    const filteredTags = this.state.tags.filter((tag) => {
      const name = (tag.name || "").toLowerCase();
      const description = (tag.description || "").toLowerCase();
      return (
        name.includes(normalizedSearch) ||
        description.includes(normalizedSearch)
      );
    });
    this.setState({ search, filteredTags });
  };

  handleShowModal = (tag = null) => {
    this.setState({
      currentTag: tag ? { ...tag } : { ...emptyTag },
      isEdit: Boolean(tag),
      showModal: true,
      error: null,
    });
  };

  handleCloseModal = () => {
    this.setState({ showModal: false, error: null });
  };

  handleFieldChange = (field, value) => {
    this.setState((prevState) => ({
      currentTag: {
        ...prevState.currentTag,
        [field]: value,
      },
    }));
  };

  saveTag = async () => {
    const storeId = this.context.storeId;
    if (!storeId) {
      this.setState({ error: "StoreId is required." });
      return;
    }

    const tag = {
      ...this.state.currentTag,
      storeId,
      tagId: Number(this.state.currentTag.tagId) || 0,
    };

    if (!tag.name) {
      this.setState({ error: "Tag name is required." });
      return;
    }

    try {
      await saveCrmItem("Tags", "AddOrUpdateTag", tag);
      await this.loadTags();
      this.handleCloseModal();
    } catch (error) {
      this.setState({ error: error.message || "Unable to save tag." });
    }
  };

  deleteTag = async (tagId) => {
    const storeId = this.context.storeId;
    if (!storeId) return;
    if (!window.confirm("Delete this tag?")) return;

    try {
      await deleteCrmItem("Tags", "DeleteTag", storeId, tagId);
      await this.loadTags();
    } catch (error) {
      this.setState({ error: error.message || "Unable to delete tag." });
    }
  };

  render() {
    const {
      filteredTags,
      loading,
      search,
      error,
      showModal,
      isEdit,
      currentTag,
    } = this.state;
    const storeId = this.context.storeId;

    return (
      <div className="m-3">
        <Row className="align-items-center mb-3">
          <Col>
            <h2>CRM Tags</h2>
            <p className="text-muted">
              Manage CRM tags for the selected store.
            </p>
          </Col>
          <Col xs="auto" className="d-flex gap-2">
            <Button variant="primary" onClick={() => this.handleShowModal()}>
              Add Tag
            </Button>
            <Button
              variant="secondary"
              onClick={this.loadTags}
              disabled={loading}
            >
              {loading ? <Spinner animation="border" size="sm" /> : "Refresh"}
            </Button>
          </Col>
        </Row>

        {!storeId && (
          <div className="alert alert-warning">
            StoreId is required to load CRM tags.
          </div>
        )}
        {error && <div className="alert alert-danger">{error}</div>}

        <Row className="mb-3">
          <Col md={6} lg={4}>
            <FormControl
              placeholder="Search by tag or description"
              value={search}
              onChange={this.onSearchChange}
            />
          </Col>
        </Row>

        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="3" className="text-center py-5">
                  <Spinner animation="border" /> Loading...
                </td>
              </tr>
            ) : filteredTags.length === 0 ? (
              <tr>
                <td colSpan="3" className="text-center py-5">
                  No tags found.
                </td>
              </tr>
            ) : (
              filteredTags.map((tag) => (
                <tr key={tag.tagId || Math.random()}>
                  <td>{tag.name || "-"}</td>
                  <td>{tag.description || "-"}</td>
                  <td>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      className="me-2"
                      onClick={() => this.handleShowModal(tag)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => this.deleteTag(tag.tagId)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>

        <Modal show={showModal} onHide={this.handleCloseModal}>
          <Modal.Header closeButton>
            <Modal.Title>{isEdit ? "Edit Tag" : "Add Tag"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3" controlId="tagName">
                <Form.Label>Tag Name</Form.Label>
                <Form.Control
                  type="text"
                  value={currentTag.name}
                  onChange={(e) =>
                    this.handleFieldChange("name", e.target.value)
                  }
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="description">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={currentTag.description}
                  onChange={(e) =>
                    this.handleFieldChange("description", e.target.value)
                  }
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" onClick={this.saveTag}>
              Save
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}
